import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { GeneratedNodeAttribute } from "@keydials/api-client";
import { Icon } from "../components/Icon";

/**
 * Authored attribute groups (KEY-436): the heading a dial's author puts a run of fields under, drawn here
 * as one bordered, collapsible card.
 *
 * The group's title is its whole identity — no key, no record — so membership is string equality on
 * `fieldsetTitle`, and a group exists exactly as long as some field names it.
 */

/** One stretch of a field list: either a field standing on its own, or a titled group of them. */
export type FieldsetRun =
  | { kind: "loose"; decl: GeneratedNodeAttribute }
  | { kind: "fieldset"; title: string; startsCollapsed: boolean; decls: GeneratedNodeAttribute[] };

/**
 * Splits a declaration list into the runs to draw, in the order given.
 *
 * A group's card is drawn where its FIRST member sits and pulls the rest in, rather than requiring the
 * members to be adjacent. Contiguity can't be relied on: the fields arrive in author order, and the
 * viewer's own access rules (KEY-299) and the action point's status gate both remove fields from this
 * list on the way here — so a group that was contiguous when authored may not be by the time it is drawn.
 * Splitting on adjacency would silently turn one card into two for exactly the viewers with the narrowest
 * access. The admin editor warns an author whose group is interrupted, since the pulled-in fields do move.
 *
 * No sorting happens here, deliberately: three of the five surfaces that call this rely on the server's
 * order rather than sorting by `order` themselves, so sorting would quietly reorder their fields.
 */
export function partitionFieldsets(decls: readonly GeneratedNodeAttribute[]): FieldsetRun[] {
  const sets: FieldsetRun[] = [];
  const loose: FieldsetRun[] = [];
  const drawn = new Set<string>();
  for (const decl of decls) {
    const title = decl.fieldsetTitle?.trim();
    if (!title) {
      loose.push({ kind: "loose", decl });
      continue;
    }
    if (drawn.has(title)) continue; // already pulled into the card drawn at its first member
    drawn.add(title);
    sets.push({
      kind: "fieldset",
      title,
      // The first member decides, because it is the one whose position the card takes.
      startsCollapsed: decl.fieldsetStartsCollapsed === true,
      decls: decls.filter((d) => d.fieldsetTitle?.trim() === title),
    });
  }
  // KEY-558: every card first, then the loose fields, whatever order they arrived in. Cards are the
  // heavier furniture — bordered, titled, collapsible — and a lone field wedged between two of them reads
  // as though it belongs to one of them. Relative order is preserved WITHIN each half, so the authored
  // order of the groups, and of the loose fields, still decides what comes first among its own kind.
  return [...sets, ...loose];
}

/**
 * How a field tells the group it sits in that it holds unsaved edits.
 *
 * Needed because a group must never hide a pending Save button: the fields keep their own draft state, so
 * a collapsed card would leave someone's typing on screen nowhere, with nothing to say it was still there.
 * Outside a group this is inert, which is why the default is a no-op rather than a required provider.
 */
const FieldsetDirtyContext = createContext<(key: string, dirty: boolean) => void>(() => {});

/**
 * Registers this field's unsaved state with the group above it, if there is one. Called by
 * `AttributeField` on every render; the group only re-renders when the set actually changes.
 */
export function useReportFieldsetDirty(attributeKey: string, dirty: boolean): void {
  const report = useContext(FieldsetDirtyContext);
  useEffect(() => {
    report(attributeKey, dirty);
    // Unmounting means the field is gone from the list entirely (a gate hid it, access changed); its
    // unsaved state went with it, so it must stop holding the card open.
    return () => report(attributeKey, false);
  }, [report, attributeKey, dirty]);
}

/**
 * A titled, bordered, collapsible card of attribute fields.
 *
 * Modelled on `SectionRingCard` in panels.tsx, with one deliberate difference: the body is ALWAYS
 * rendered and hidden with `display: none`, never unmounted. Each `AttributeField` owns its draft, its
 * in-flight save and its "I dismissed the suggested default" decision; unmounting a collapsed card would
 * abort a save in progress and throw away edits nobody had been warned about.
 *
 * `startsCollapsed` is the arrival state, not a lock. The card opens itself while the status gate is
 * waiting on a field inside it — otherwise a collapsed group dead-ends the guided fill, which points at a
 * field the reader cannot see — and while any field inside holds unsaved edits.
 *
 * `reveal` is the gentler signal (KEY-569): someone picked a field inside from the Overview list, so the card
 * arrives open, but it isn't held — no "needs an answer" chip, and the reader can fold it away again. It opens
 * on mount rather than in a later effect because the field is scrolled to straight away, and scrolling to an
 * element inside a `display: none` card does nothing.
 */
export function CollapsibleFieldset({ title, startsCollapsed, forceOpen = false, reveal = false, children }: {
  title: string;
  startsCollapsed: boolean;
  forceOpen?: boolean;
  reveal?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(!startsCollapsed || reveal);
  useEffect(() => {
    if (reveal) {
      setOpen(true);
    }
  }, [reveal]);
  const [dirtyKeys, setDirtyKeys] = useState<ReadonlySet<string>>(() => new Set());

  const report = useCallback((key: string, dirty: boolean) => {
    setDirtyKeys((prev) => {
      if (prev.has(key) === dirty) return prev; // no change — don't re-render the card on every keystroke
      const next = new Set(prev);
      if (dirty) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const held = forceOpen || dirtyKeys.size > 0;
  const expanded = open || held;
  const label = expanded ? `Collapse ${title}` : `Expand ${title}`;

  return (
    <div className="kd-card">
      <button
        type="button"
        className="kd-focusable"
        onClick={() => setOpen(!expanded)}
        aria-expanded={expanded}
        aria-label={label}
        title={held && !open
          ? (forceOpen ? "Stays open while a status is waiting on a field in here" : "Stays open while there are unsaved changes in here")
          : undefined}
        style={{
          width: "100%",
          font: "inherit",
          cursor: "pointer",
          border: "none",
          background: "none",
          padding: "12px 14px",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {title}
        </span>
        {/* Says why the chevron won't shut it — without this the toggle just looks broken. */}
        {held && !open && (
          <span style={{ flex: "none", fontSize: 11.5, fontWeight: 600, color: "var(--text-faint)" }}>
            {forceOpen ? "needs an answer" : "unsaved"}
          </span>
        )}
        <Icon
          name="chevDown"
          size={16}
          style={{ flex: "none", color: "var(--text-faint)", transition: "transform .25s ease", transform: expanded ? "rotate(0deg)" : "rotate(-90deg)" }}
        />
      </button>
      {/* Always mounted — see the note above. `display` rather than the `hidden` attribute, because the
          explicit flex below would override what `hidden` sets. */}
      <div
        style={{
          display: expanded ? "flex" : "none",
          flexDirection: "column",
          gap: 12,
          padding: "0 14px 14px",
          borderTop: "var(--border-w) solid var(--border)",
          paddingTop: 12,
        }}
      >
        <FieldsetDirtyContext.Provider value={report}>{children}</FieldsetDirtyContext.Provider>
      </div>
    </div>
  );
}

/**
 * Draws a declaration list with its authored groups as cards and everything else flat — the one seam all
 * five attribute surfaces go through, so grouping behaves identically on each.
 *
 * `render` stays with the caller because every surface builds its own field differently: they each have
 * their own value lookup, scope, save request and (on an action point) gate highlighting.
 */
export function FieldsetRuns({ decls, render, demandedKeys, revealKeys }: {
  decls: readonly GeneratedNodeAttribute[];
  render: (decl: GeneratedNodeAttribute) => ReactNode;
  /** The keys a parked status pick is waiting on, if this surface has a gate. */
  demandedKeys?: ReadonlySet<string>;
  /** Keys of fields someone has just been sent to (KEY-569): their groups arrive open, without being held. */
  revealKeys?: ReadonlySet<string>;
}) {
  const runs = useMemo(() => partitionFieldsets(decls), [decls]);
  // Nothing is grouped: render exactly what the caller rendered before this existed, with no wrapper at
  // all. Worth keeping literal — every dial authored before KEY-436 takes this path.
  if (runs.every((r) => r.kind === "loose")) {
    return <>{decls.map((d) => render(d))}</>;
  }
  return (
    <>
      {runs.map((run) =>
        run.kind === "loose" ? (
          <div key={`loose:${run.decl.key}`}>{render(run.decl)}</div>
        ) : (
          <CollapsibleFieldset
            key={`set:${run.title}`}
            title={run.title}
            startsCollapsed={run.startsCollapsed}
            forceOpen={!!demandedKeys && run.decls.some((d) => demandedKeys.has(d.key))}
            reveal={!!revealKeys && run.decls.some((d) => revealKeys.has(d.key))}
          >
            {run.decls.map((d) => render(d))}
          </CollapsibleFieldset>
        ))}
    </>
  );
}
